import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { crmFetch } from './crmApi';

const EMPTY = {
  name: '', email: '', phone: '', website: '', city: '', country: 'Germany',
  industry: '', companySize: '', description: '',
};

const COUNTRIES = ['Germany', 'Austria', 'Switzerland', 'Netherlands', 'Belgium', 'France', 'United Kingdom', 'Other'];
const INDUSTRIES = ['Technology', 'Finance', 'Healthcare', 'Manufacturing', 'Retail', 'E-commerce', 'Consulting', 'Marketing', 'Education', 'Real Estate', 'Automotive', 'Energy', 'Telecommunications', 'Other'];
const SIZES = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1001-5000', '5001+'];

export const OrganizationModal = ({ open, onOpenChange, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(EMPTY); }, [open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Company name is required'); return; }
    if (!form.email.trim()) { toast.error('Company email is required'); return; }
    setSaving(true);
    try {
      const res = await crmFetch('/organizations', { method: 'POST', body: JSON.stringify(form) });
      if (!res.success) { toast.error(res.message || 'Failed to create company'); return; }
      toast.success('Company added to the platform 🎉');
      onOpenChange(false);
      onSaved && onSaved(res.data);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="organization-modal">
        <DialogHeader>
          <DialogTitle>Add Company</DialogTitle>
          <DialogDescription>Onboard a new client company onto the JobastiIQ platform.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">Company Name *</Label>
              <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Acme Corporation" data-testid="org-name-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="contact@acme.com" data-testid="org-email-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Phone</Label>
              <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+49 …" data-testid="org-phone-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Website</Label>
              <Input value={form.website} onChange={(e) => set('website', e.target.value)} placeholder="https://acme.com" data-testid="org-website-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">City</Label>
              <Input value={form.city} onChange={(e) => set('city', e.target.value)} placeholder="Berlin" data-testid="org-city-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Country</Label>
              <Select value={form.country} onValueChange={(v) => set('country', v)}>
                <SelectTrigger data-testid="org-country-select"><SelectValue /></SelectTrigger>
                <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">Industry</Label>
              <Select value={form.industry || 'none'} onValueChange={(v) => set('industry', v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="org-industry-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {INDUSTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">Company Size</Label>
              <Select value={form.companySize || 'none'} onValueChange={(v) => set('companySize', v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="org-size-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {SIZES.map((c) => <SelectItem key={c} value={c}>{c} employees</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block">Description</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Short description of the company…" data-testid="org-description-input" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="org-cancel-btn">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="org-save-btn">
              {saving ? 'Creating…' : 'Add Company'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
